import { FC, useEffect, useState } from "react";
import { usePaymentVoucherStore } from "../../hooks/usePaymentVoucherStore";
import { moneyCL } from "../../toolbox/helpers/money-helpers";
import { formatDate, formatDateTime } from "../../toolbox/helpers/formatDateHelper";
import { Breadcrumbs, Grid } from "@mui/material";
import { emphasize, styled } from '@mui/material/styles';
import Chip from '@mui/material/Chip';
import CustomTable from "../../components/common/CustomTable/CustomTable";
import { ApiStatus } from "../../types/api/status";
import { PaymentVoucherFormDrawer } from "./components/drawers/PaymentVoucherFormDrawer";
import { ModalConfirm } from "../../components/common/ModalConfirm/ModalConfirm";

const StyledBreadcrumb = styled(Chip)(({ theme }) => {
    const backgroundColor =
        theme.palette.mode === 'light'
            ? theme.palette.grey[100]
            : theme.palette.grey[800];
    return {
        backgroundColor,
        height: theme.spacing(3),
        color: theme.palette.text.primary,
        fontWeight: theme.typography.fontWeightRegular,
        '&:hover, &:focus': {
            backgroundColor: emphasize(backgroundColor, 0.06),
        },
        '&:active': {
            boxShadow: theme.shadows[1],
            backgroundColor: emphasize(backgroundColor, 0.12),
        },
    };
})

interface onList {
    page?: number
    perPage?: number
    search?: string
    startDate?: string
    endDate?: string
}

const columns = [
    { type: 'options', field: 'options', label: '', align: 'center' },
    { type: 'text', field: 'correlative', label: 'Correlativo', align: 'center' },
    { type: 'text', field: 'rut', label: 'RUT', align: 'center' },
    { type: 'text', field: 'supplier', label: 'Cliente/Proveedor', align: 'center' },
    { type: 'text', field: 'way_to_pay', label: 'Método de pago', align: 'center' },
    { type: 'text', field: 'series', label: 'Serie', align: 'center', format: (row) => row.series ?? '-' },
    { type: 'text', field: 'document_date', label: 'Fecha de cobro', align: 'center', format: (row) => row.document_date ? formatDate(row.document_date) : '-' },
    { type: 'text', field: 'amount', label: 'Monto', align: 'center', format: (row) => moneyCL(row.amount) },
    { type: 'text', field: 'created_at', label: 'Fecha de registro', align: 'center', format: (row) => formatDateTime(row.created_at) },
    { type: 'detail', field: 'detail', label: 'PDF', align: 'center' },
];

const today = new Date().toISOString().split('T')[0]

export const PaymentEnteredList: FC = () => {
    const { status: statusVoucher, paymentVouchers, totalRows, getPaymentVouchers,
        cancelPaymentVoucher
    } = usePaymentVoucherStore()

    const [rowSelected, setRowSelected] = useState<any>('')
    const [showModalConfirm, setShowModalConfirm] = useState<boolean>(false)
    const [showDrawer, setShowDrawer] = useState<boolean>(false)

    const [search, setSearch] = useState<string>('')
    const [startDate, setStartDate] = useState<string>(today)
    const [endDate, setEndDate] = useState<string>(today)
    const [page, setPage] = useState<number>(1)
    const [perPage, setPerPage] = useState<number>(10)

    useEffect(() => {
        if (search === '') {
            getDataAPI()
        } else {
            const timer = setTimeout(() => getDataAPI(), 500)
            return () => clearTimeout(timer)
        }
    }, [page, perPage, search, startDate, endDate])

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'F2') {
                event.preventDefault()
                onAdd()
            }
        }

        window.addEventListener('keydown', handleKeyDown)
        return () => {
            window.removeEventListener('keydown', handleKeyDown)
        }
    }, [])

    const getDataAPI = async () => {
        const payload: onList = {
            page: page,
            perPage: perPage,
        }
        if (search !== '') payload.search = search
        if (startDate !== '') payload.startDate = startDate
        if (endDate !== '') payload.endDate = endDate

        await getPaymentVouchers(payload)
    }

    const handleChangePage = (event: any, newPage: any) => {
        setPage(newPage + 1)
    }

    const handleChangeRowsPerPage = (event) => {
        setPerPage(+event.target.value)
        setPage(1)
    }

    const onSearch = (search) => {
        setSearch(search)
    }

    const onFilterStartDate = (date) => {
        setStartDate(date)
    }

    const onFilterEndDate = (date) => {
        setEndDate(date)
    }

    const onAdd = () => {
        setShowDrawer(true)
    }

    const onDelete = (rowSelected) => {
        setRowSelected(rowSelected)
        setShowModalConfirm(true)
    }

    const onDeleteConfirm = async () => {
        const resp = await cancelPaymentVoucher(rowSelected?.id)
        if (resp === true) {
            getDataAPI()
            setShowModalConfirm(false)
        }
    }

    const onDetails = (rowSelected) => {
        if (!rowSelected?.url_document) return

        window.open(process.env.REACT_APP_ROOT_URL + rowSelected.url_document, '_blank')
    }

    return (
        <>
            <Breadcrumbs aria-label="breadcrumb" sx={{ mt: 3 }}>
                <StyledBreadcrumb
                    label={"Contabilidad"}
                />
                <StyledBreadcrumb
                    label={"Libro de comprobantes"}
                    onClick={() => {
                        getDataAPI()
                    }}
                />
            </Breadcrumbs>

            <Grid item xs={12}>
                <CustomTable
                    title={'Libro de comprobantes'}
                    columns={columns}
                    loading={statusVoucher === ApiStatus.FETCHING}
                    rows={paymentVouchers || []}
                    onRowClick={() => { }}
                    hasOptions
                    onAddFn={onAdd}
                    onAddFnName={'Registrar nuevo pago'}
                    onDetails={onDetails}
                    onSearch={onSearch}
                    onDelete={onDelete}

                    startDate={startDate} 
                    endDate={endDate}
                    onFilterStartDate={onFilterStartDate}
                    onFilterEndDate={onFilterEndDate}

                    totalRows={totalRows}
                    rowsPerPage={perPage}
                    page={page}
                    handleChangePage={handleChangePage}
                    handleChangeRowsPerPage={handleChangeRowsPerPage}
                    withCustomPagination
                    dense
                />
            </Grid>
            {showDrawer && (
                <PaymentVoucherFormDrawer
                    open={showDrawer}
                    onClose={() => setShowDrawer(false)}
                    onList={getDataAPI}
                />
            )}
            {
                showModalConfirm && (
                    <ModalConfirm
                        open={showModalConfirm}
                        closeModal={() => { setShowModalConfirm(false) }}
                        onConfirm={onDeleteConfirm}
                        status={statusVoucher === ApiStatus.FETCHING}
                        title="¿Está seguro de anular el comprobante?"
                        text='Si estás seguro, haz click en el botón "Aceptar" y el comprobante será anulado.'
                    />
                )
            }
        </>
    )
}
